import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
} from '@heroicons/react/16/solid';
import { DocumentField, DocumentType } from 'src/services/documents/types';
import SearchBox from './SearchBox';
import { ruleOperators } from 'src/utils/constants/checklist-rules-constants';
import { UseQueryResult } from '@tanstack/react-query';
import { cn } from 'src/utils/cn';
import { useLoanStore } from 'src/stores/loan';
import { ExternalField } from 'src/services/fields/types';
import { useMemo, useState } from 'react';
import { TableColumnSelectionModal } from './TableColumnSelectionModal';
import { VersionSelectionModal } from './VersionSelectionModal';
import SyntaxHelperModal from './SyntaxHelperModal';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import Button from 'src/components/Button';

interface RuleComposerProps {
  handleFieldSelect: (
    type: 'docField' | 'losField' | 'tableColumn',
    item: string,
    definition?: string,
  ) => void;
  externalFieldQuery: UseQueryResult<ExternalField[], Error>;
  sharedFieldQuery: UseQueryResult<
    {
      documentTypes: DocumentType[];
      documentTypesObj: Record<string, DocumentType>;
    },
    Error
  >;
  updateRule: (
    type:
      | 'operator'
      | 'docField'
      | 'losField'
      | 'date'
      | 'text'
      | 'UNDO'
      | 'REDO',
    value: string,
  ) => void;
  validExpression: boolean;
  userOnChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleRuleComposerCurorChange: (
    e:
      | React.MouseEvent<HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLTextAreaElement>,
  ) => void;
  rulComposerRef: React.RefObject<HTMLTextAreaElement | null>;
  isTestable: boolean;
  openTestDialog: () => void;
  expression?: string;
  showTestButton: boolean;
}

function FieldSelector({
  buttonName,
  onSelect,
  options,
  classNames = '',
  childClassNames = '',
}: Readonly<{
  type: 'docField' | 'losField' | 'date' | 'text' | 'tableColumn';
  buttonName: string;
  onSelect: (item: string) => void;
  options: string[];
  classNames?: string;
  childClassNames?: string;
}>) {
  return (
    <div className={cn('flex', classNames)}>
      <SearchBox
        buttonName={buttonName}
        onSelect={(item) => onSelect(item)}
        options={options}
        classNames={childClassNames}
      />
    </div>
  );
}

// Helper function to format a single table schema item
function formatTableSchemaItem(
  docTypeDisplayName: string,
  fieldDisplayName: string,
  schemaItem: { displayName?: string; key: string },
): string {
  const schemaDisplay =
    schemaItem.displayName && schemaItem.displayName.trim() !== ''
      ? schemaItem.displayName
      : schemaItem.key;

  return `${docTypeDisplayName}.${fieldDisplayName}.${schemaDisplay}`;
}

// Helper function to process a document field's table schema
function processFieldTableSchema(
  docTypeDisplayName: string,
  field: DocumentField,
): string[] {
  if (!field.tableSchema || field.tableSchema.length === 0) {
    return [];
  }

  return field.tableSchema.map((schemaItem) =>
    formatTableSchemaItem(docTypeDisplayName, field.displayName, schemaItem),
  );
}

export function RuleComposer({
  handleFieldSelect,
  updateRule,
  validExpression,
  userOnChange,
  handleRuleComposerCurorChange,
  rulComposerRef,
  externalFieldQuery,
  sharedFieldQuery,
  openTestDialog,
  expression,
  showTestButton = true,
}: Readonly<RuleComposerProps>) {
  const { currentChecklistRule } = useLoanStore();
  const expressionValue =
    expression ?? currentChecklistRule?.definition?.expression ?? '';
  const [showTableColumnModal, setShowTableColumnModal] = useState(false);
  const [showSyntaxHelper, setShowSyntaxHelper] = useState(false);
  const [localCursor, setLocalCursor] = useState(0);
  const [pendingTableColumn, setPendingTableColumn] = useState<string | null>(
    null,
  );
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [pendingVersionItem, setPendingVersionItem] = useState<{
    type: 'docField' | 'tableColumn';
    item: string;
    definition?: string;
  } | null>(null);
  const [versionLabels, setVersionLabels] = useState<
    { internal: string; display: string }[]
  >([]);

  const handleInterceptFieldSelect = (
    type: 'docField' | 'tableColumn',
    item: string,
    definition?: string,
  ) => {
    const docTypeDisplayName = item.split('.')[0];
    const docType = sharedFieldQuery.data?.documentTypes.find(
      (dt) => dt.displayName === docTypeDisplayName,
    );

    if (
      docType?.versionerConfiguration?.labels &&
      docType.versionerConfiguration.labels.length > 0
    ) {
      setVersionLabels(docType.versionerConfiguration.labels);
      setPendingVersionItem({ type, item, definition });
      setShowVersionModal(true);
    } else {
      handleFieldSelect(type, item, definition);
    }
  };

  const handleVersionConfirm = (internalVersion: string | null) => {
    if (pendingVersionItem) {
      let finalItem = pendingVersionItem.item;
      if (internalVersion) {
        const parts = finalItem.split('.');
        parts[0] = `${parts[0]}:${internalVersion}`;
        finalItem = parts.join('.');
      }
      handleFieldSelect(
        pendingVersionItem.type,
        finalItem,
        pendingVersionItem.definition,
      );
    }
    setShowVersionModal(false);
    setPendingVersionItem(null);
  };

  const updateLocalCursor = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    setLocalCursor(e.currentTarget.selectionStart);
  };

  const getMatchingBracketIndex = (
    text: string,
    cursor: number,
  ): { start: number; end: number } | null => {
    if (!text || cursor < 0 || cursor > text.length) return null;
    const pairs: Record<string, string> = { '(': ')', '[': ']' };
    const inversePairs: Record<string, string> = { ')': '(', ']': '[' };

    let checkPos = -1;
    let char = '';

    if (
      cursor < text.length &&
      (pairs[text[cursor]] || inversePairs[text[cursor]])
    ) {
      checkPos = cursor;
      char = text[cursor];
    } else if (
      cursor > 0 &&
      (pairs[text[cursor - 1]] || inversePairs[text[cursor - 1]])
    ) {
      checkPos = cursor - 1;
      char = text[cursor - 1];
    }

    if (checkPos === -1) return null;

    if (pairs[char]) {
      let depth = 0;
      for (let i = checkPos; i < text.length; i++) {
        if (text[i] === char) depth++;
        else if (text[i] === pairs[char]) depth--;
        if (depth === 0) return { start: checkPos, end: i };
      }
    } else {
      let depth = 0;
      for (let i = checkPos; i >= 0; i--) {
        if (text[i] === char) depth++;
        else if (text[i] === inversePairs[char]) depth--;
        if (depth === 0) return { start: i, end: checkPos };
      }
    }
    return null;
  };

  const renderHighlightedExpression = () => {
    const match = getMatchingBracketIndex(expressionValue, localCursor);

    if (!match) {
      return <span>{expressionValue}</span>;
    }

    const start = Math.min(match.start, match.end);
    const end = Math.max(match.start, match.end);

    return (
      <>
        <span>{expressionValue.substring(0, start)}</span>
        <span className="bg-blue-500 text-transparent rounded-[2px] shadow-sm shadow-blue-500/50">
          {expressionValue[start]}
        </span>
        <span>{expressionValue.substring(start + 1, end)}</span>
        <span className="bg-blue-500 text-transparent rounded-[2px] shadow-sm shadow-blue-500/50">
          {expressionValue[end]}
        </span>
        <span>{expressionValue.substring(end + 1)}</span>
      </>
    );
  };

  const fieldOptions = useMemo(() => {
    const documentFields: string[] = [];
    sharedFieldQuery.data?.documentTypes.forEach(
      (tenantDocType: DocumentType) => {
        tenantDocType.fields.forEach((tenantfield: DocumentField) => {
          documentFields.push(
            `${tenantDocType.displayName}.${tenantfield.displayName}`,
          );
        });
      },
    );
    return documentFields;
  }, [sharedFieldQuery.data]);

  const documentTypeOptions = useMemo(() => {
    return [
      ...new Set(
        sharedFieldQuery.data?.documentTypes.map(
          (tenantDocType: DocumentType) => tenantDocType.displayName,
        ) ?? [],
      ),
    ];
  }, [sharedFieldQuery.data]);

  const tableColumnOptions = useMemo(() => {
    if (!sharedFieldQuery.data?.documentTypes) {
      return [];
    }

    return sharedFieldQuery.data.documentTypes.flatMap(
      (tenantDocType: DocumentType) =>
        tenantDocType.fields.flatMap((tenantfield: DocumentField) =>
          processFieldTableSchema(tenantDocType.displayName, tenantfield),
        ),
    );
  }, [sharedFieldQuery.data]);

  const externalFields = useMemo(() => {
    return (
      externalFieldQuery.data?.map((field: ExternalField) =>
        field.metadata?.source_field_id
          ? `#${field.metadata.source_field_id.replace('#', '')}#.${field.name}`
          : null,
      ) ?? []
    ).filter((value): value is string => Boolean(value));
  }, [externalFieldQuery.data]);

  const handleTableColumnConfirm = (definition: string) => {
    if (!pendingTableColumn) return;
    const item = pendingTableColumn;
    setShowTableColumnModal(false);
    setPendingTableColumn(null);
    handleInterceptFieldSelect('tableColumn', item, definition);
  };

  const handleTableColumnCancel = () => {
    setShowTableColumnModal(false);
    setPendingTableColumn(null);
  };

  return (
    <div className="flex flex-col h-[100%] min-h-0">
      <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-2 shrink-0">
        <div>
          <h2 className="text-base font-semibold leading-6 text-gray-900">
            Rule Expression Composer
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="px-3 py-1 text-xs text-gray-700 items-center gap-x-1.5 hover:border-gray-400 hover:bg-gray-50"
            icon={
              <QuestionMarkCircleIcon
                className="-ml-0.5 h-3 w-3 text-gray-400"
                aria-hidden="true"
              />
            }
            onClick={() => setShowSyntaxHelper(true)}
          >
            Syntax Help
          </Button>
          {showTestButton && (
            <Button
              variant="contained"
              className="px-3 py-1 text-xs text-white items-center"
              id="btn-test-rule"
              data-testid="btn-test-rule"
              onClick={openTestDialog}
              disabled={!validExpression}
            >
              Verify Rule
            </Button>
          )}
        </div>
      </div>

      {/* Field Selectors Toolbar */}
      <div className="bg-gray-50/80 rounded p-2 ring-1 ring-inset ring-gray-200 mb-4 shadow-sm shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
          <FieldSelector
            type="docField"
            buttonName="Document_Type"
            onSelect={(item) => handleInterceptFieldSelect('docField', item)}
            options={documentTypeOptions}
          />
          <FieldSelector
            type="docField"
            buttonName="Doc_Field"
            onSelect={(item) => handleInterceptFieldSelect('docField', item)}
            options={fieldOptions}
          />
          <FieldSelector
            type="tableColumn"
            buttonName="TableColumn_Field"
            onSelect={(item) => {
              setPendingTableColumn(item);
              setShowTableColumnModal(true);
            }}
            options={tableColumnOptions}
          />
          <FieldSelector
            type="losField"
            buttonName="LOS_Field"
            onSelect={(item) => handleFieldSelect('losField', item)}
            options={externalFields}
          />
          <FieldSelector
            type="date"
            buttonName="Date"
            onSelect={(item) => updateRule('date', `'${item}'`)}
            options={[]}
          />
          <FieldSelector
            type="text"
            buttonName="Text"
            onSelect={(item) => updateRule('text', `'${item}'`)}
            options={[]}
          />
        </div>
      </div>

      {/* Editor Area */}
      <div className="flex flex-col flex-1 gap-2 min-h-0">
        {/* Visual Indicator of Validity */}
        <div
          className={cn(
            'rounded py-1.5 px-3 flex items-center justify-between transition-colors shrink-0',
            validExpression
              ? 'bg-green-50 ring-1 ring-green-200'
              : 'bg-red-50 ring-1 ring-red-200',
          )}
        >
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'flex-shrink-0',
                validExpression ? 'text-green-500' : 'text-red-500',
              )}
            >
              {validExpression ? (
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <h3
              className={cn(
                'text-xs font-medium',
                validExpression ? 'text-green-800' : 'text-red-800',
              )}
            >
              {validExpression
                ? 'Valid Code Block'
                : 'Error parsing the current rule'}
            </h3>
          </div>
          <p
            className={cn(
              'text-[10px]',
              validExpression ? 'text-green-700' : 'text-red-700',
            )}
          >
            {expressionValue
              ? validExpression
                ? 'Expression meets basic validation requirements.'
                : 'Check your syntax, brackets, and field names.'
              : 'Expression is empty.'}
          </p>
        </div>

        {/* Operators */}
        <div className="flex flex-wrap items-center justify-between gap-1 shrink-0">
          <div className="flex flex-wrap gap-1">
            {ruleOperators.map((operator, index) => (
              <button
                key={'btn-operator-' + index}
                data-testid={'btn-operator-' + operator}
                id={'btn-operator-' + operator}
                className="inline-flex items-center rounded bg-white px-2 py-1 text-[11px] font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 shadow-sm transition-all focus:z-10"
                onClick={() => updateRule('operator', operator)}
              >
                {operator}
              </button>
            ))}
          </div>

          <div className="flex items-center shadow-sm">
            <button
              onClick={() => updateRule('UNDO', '')}
              className="inline-flex items-center rounded-l bg-white px-2 py-1 text-[11px] font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
              title="Undo"
            >
              <ArrowUturnLeftIcon className="h-3 w-3 text-gray-500" />
            </button>
            <button
              onClick={() => updateRule('REDO', '')}
              className="-ml-px inline-flex items-center rounded-r bg-white px-2 py-1 text-[11px] font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
              title="Redo"
            >
              <ArrowUturnRightIcon className="h-3 w-3 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Text Area */}
        <div
          className={cn(
            'relative flex-1 min-h-[100px] w-full rounded border-1 shadow-inner font-mono transition-colors overflow-hidden',
            validExpression
              ? 'border-gray-300 focus-within:border-gray-500'
              : 'border-red-300 focus-within:border-red-600',
          )}
        >
          {/* Custom syntax highlighting background layer */}
          <div
            className="absolute inset-0 pointer-events-none whitespace-pre-wrap break-words text-sm text-transparent p-4"
            style={{
              margin: 0,
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              lineHeight: '1.6',
            }}
            aria-hidden="true"
          >
            {renderHighlightedExpression()}
          </div>
          <textarea
            ref={rulComposerRef}
            className="absolute inset-0 min-w-full h-full resize-none bg-transparent p-4 text-sm outline-none focus:outline-none focus:ring-0"
            style={{
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              lineHeight: '1.6',
            }}
            value={expressionValue}
            onChange={(e) => {
              userOnChange(e);
              updateLocalCursor(e);
            }}
            onClick={(e) => {
              handleRuleComposerCurorChange(e);
              updateLocalCursor(e);
            }}
            onKeyUp={(e) => {
              handleRuleComposerCurorChange(e);
              updateLocalCursor(e);
            }}
            onSelect={updateLocalCursor}
            placeholder="Start typing your rule expression here..."
          />
        </div>
      </div>

      <TableColumnSelectionModal
        open={showTableColumnModal}
        onClose={handleTableColumnCancel}
        onConfirm={handleTableColumnConfirm}
        tableColumnItem={pendingTableColumn || ''}
      />
      <VersionSelectionModal
        open={showVersionModal}
        onClose={() => {
          setShowVersionModal(false);
          setPendingVersionItem(null);
        }}
        onConfirm={handleVersionConfirm}
        labels={versionLabels}
      />
      <SyntaxHelperModal
        open={showSyntaxHelper}
        onClose={() => setShowSyntaxHelper(false)}
      />
    </div>
  );
}
